import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Calendar, MapPin, Users, QrCode, Upload, Download, X, Check,
  Image as ImageIcon, ArrowLeft, Trash2, CheckSquare, Square, Edit, Save
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventDetails() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qrToken = searchParams.get('token');
  
  const [event, setEvent] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editCoverImageFile, setEditCoverImageFile] = useState(null);
  const [editCoverImagePreview, setEditCoverImagePreview] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [canUploadPhotos, setCanUploadPhotos] = useState(false);
  const [timeUntilEvent, setTimeUntilEvent] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadEventDetails();
    verifyAccess();
    checkRegistrationStatus();
  }, [eventId]);

  useEffect(() => {
    if (event) {
      checkEventTiming();
    }
  }, [event]);

  const loadEventDetails = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`);
      setEvent(response.data.event);
      
      // Try to get QR code (no auth required)
      try {
        const qrResponse = await axios.get(`${API}/events/${eventId}/qr-code`);
        if (qrResponse.data.success) {
          setQrCode(qrResponse.data.qrCode);
        }
      } catch (error) {
        console.log('QR code not available yet:', error.response?.data?.message);
      }
    } catch (error) {
      console.error('Failed to load event:', error);
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const verifyAccess = async () => {
    // Everyone has access to photos now (no auth required)
    setHasAccess(true);
    loadPhotos();
  };

  const checkRegistrationStatus = async () => {
    if (!token) return;
    
    try {
      const userId = localStorage.getItem('userId');
      const response = await axios.get(`${API}/events/${eventId}/register`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Check if already registered
      if (response.data.message === 'Already registered') {
        setIsRegistered(true);
      }
    } catch (error) {
      console.log('Not registered for this event');
    }
  };

  const checkEventTiming = () => {
    if (!event) return;
    
    const eventDateTime = new Date(`${event.date}T${event.time}`);
    const now = new Date();
    
    if (now >= eventDateTime) {
      setCanUploadPhotos(true);
      setTimeUntilEvent('');
    } else {
      setCanUploadPhotos(false);
      const diff = eventDateTime - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);
      
      if (days > 0) {
        setTimeUntilEvent(`Event starts in ${days} day${days > 1 ? 's' : ''}`);
      } else {
        setTimeUntilEvent(`Event starts in ${hours} hour${hours > 1 ? 's' : ''}`);
      }
    }
  };

  const loadPhotos = async () => {
    try {
      const params = new URLSearchParams();
      if (qrToken) params.append('token', qrToken);
      
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/events/${eventId}/photos?${params.toString()}`, { headers });
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Failed to load photos:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    if (!canUploadPhotos) {
      toast.error('Photo uploads are only allowed after the event starts');
      return;
    }
    
    setUploading(true);
    let successCount = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (qrToken) formData.append('token', qrToken);
        
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const params = qrToken ? `?token=${qrToken}` : '';
        await axios.post(`${API}/events/${eventId}/photos/upload${params}`, formData, { 
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
      } catch (error) {
        console.error('Failed to upload photo:', error);
        const errorMsg = error.response?.data?.detail || `Failed to upload ${file.name}`;
        toast.error(errorMsg);
      }
    }
    
    setUploading(false);
    if (successCount > 0) {
      toast.success(`${successCount} photo(s) uploaded successfully!`);
      loadPhotos();
    }
  };

  const downloadPhoto = (photoId, fileName) => {
    window.open(`${API}/events/${eventId}/photos/${photoId}/download`, '_blank');
  };

  const downloadSelectedPhotos = async () => {
    if (selectedPhotos.length === 0) {
      toast.error('Please select photos to download');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/events/${eventId}/photos/download-multiple`,
        selectedPhotos,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `event_${eventId}_photos.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Photos downloaded!');
      setSelectedPhotos([]);
    } catch (error) {
      console.error('Failed to download photos:', error);
      toast.error('Failed to download photos');
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/events/${eventId}/photos/${photoId}`, { headers });
      toast.success('Photo deleted!');
      loadPhotos();
    } catch (error) {
      console.error('Failed to delete photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const selectAllPhotos = () => {
    if (selectedPhotos.length === photos.length) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(photos.map(p => p.id));
    }
  };

  const downloadQRCode = () => {
    if (!qrCode) return;
    
    // Create a temporary link to download the QR code
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `event-qr-code-${event.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR Code downloaded!');
  };

  const startEditing = () => {
    setEditForm({
      name: event.name,
      date: event.date,
      time: event.time,
      endTime: event.endTime || '',
      location: event.location,
      description: event.description,
      category: event.category,
      capacity: event.capacity || '',
      ticketPrice: event.ticketPrice || 0,
      isPublic: event.isPublic,
      requiresRegistration: event.requiresRegistration
    });
    setEditCoverImagePreview(event.coverImage ? `${API}/${event.coverImage}` : null);
    setIsEditing(true);
  };

  const handleEditCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditCoverImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditCoverImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveEventChanges = async () => {
    try {
      await axios.put(
        `${API}/events/${eventId}`,
        {
          ...editForm,
          capacity: editForm.capacity ? parseInt(editForm.capacity) : null,
          ticketPrice: parseFloat(editForm.ticketPrice || 0)
        }
      );
      
      // Upload new cover image if selected
      if (editCoverImageFile) {
        const formData = new FormData();
        formData.append('file', editCoverImageFile);
        await axios.post(`${API}/events/${eventId}/cover-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Event updated successfully!');
      setIsEditing(false);
      setEditCoverImageFile(null);
      setEditCoverImagePreview(null);
      loadEventDetails();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error('Failed to update event');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading event...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Event not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/events')}
          variant="ghost"
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>

        {/* Event Info */}
        <Card className="mb-6 overflow-hidden">
          {event.coverImage && (
            <div className="w-full h-64 overflow-hidden">
              <img 
                src={`${API}/${event.coverImage}`} 
                alt={event.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-3xl mb-2">{event.name}</CardTitle>
                <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                  {event.category}
                </span>
              </div>
              {!isRegistered && (
                <Button
                  onClick={startEditing}
                  variant="outline"
                  className="text-purple-600 hover:bg-purple-50"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Event
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="flex items-center text-gray-700">
                <Calendar className="w-5 h-5 mr-3 text-purple-600" />
                <div>
                  <p className="font-medium">{new Date(event.date).toLocaleDateString()}</p>
                  <p className="text-sm text-gray-500">{event.time}</p>
                </div>
              </div>
              <div className="flex items-center text-gray-700">
                <MapPin className="w-5 h-5 mr-3 text-purple-600" />
                <div>
                  <p className="font-medium">{event.location}</p>
                </div>
              </div>
              <div className="flex items-center text-gray-700">
                <Users className="w-5 h-5 mr-3 text-purple-600" />
                <div>
                  <p className="font-medium">{event.attendeeCount || 0} Attendees</p>
                  {event.capacity && <p className="text-sm text-gray-500">Max: {event.capacity}</p>}
                </div>
              </div>
            </div>
            <p className="text-gray-600 mb-4">{event.description}</p>
          </CardContent>
        </Card>

        {/* QR Code */}
        {qrCode && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="w-5 h-5" />
                  Event QR Code for Photo Sharing
                </CardTitle>
                <Button
                  onClick={downloadQRCode}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download QR Code
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <img src={qrCode} alt="Event QR Code" className="w-48 h-48 border-2 border-gray-200 rounded-lg" />
                <div>
                  <p className="text-gray-700 mb-2">📱 Share this QR code with attendees!</p>
                  <p className="text-sm text-gray-500 mb-4">
                    • Available 1 day before event<br />
                    • Anyone can scan to upload and view event photos<br />
                    • Photos auto-delete 6 days after event ends
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={downloadQRCode}
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:bg-green-50"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photo Gallery */}
        {hasAccess && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Event Photos ({photos.length})
                </CardTitle>
                <div className="flex gap-2">
                  {selectedPhotos.length > 0 && (
                    <Button
                      onClick={downloadSelectedPhotos}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download {selectedPhotos.length} Selected
                    </Button>
                  )}
                  {canUploadPhotos ? (
                    <>
                      <Button
                        onClick={() => document.getElementById('photo-upload').click()}
                        className="bg-purple-600 hover:bg-purple-700"
                        disabled={uploading}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload Photos'}
                      </Button>
                      <input
                        id="photo-upload"
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </>
                  ) : (
                    <div className="px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        📸 Photo uploads available after event starts • {timeUntilEvent}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {photos.length > 0 && (
                <div className="mb-4 flex items-center gap-4">
                  <Button
                    onClick={selectAllPhotos}
                    variant="outline"
                    size="sm"
                  >
                    {selectedPhotos.length === photos.length ? (
                      <>
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Deselect All
                      </>
                    ) : (
                      <>
                        <Square className="w-4 h-4 mr-2" />
                        Select All
                      </>
                    )}
                  </Button>
                  <span className="text-sm text-gray-600">
                    {selectedPhotos.length} of {photos.length} selected
                  </span>
                </div>
              )}

              {photos.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No photos yet</p>
                  <p className="text-sm text-gray-400">Be the first to upload event photos!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {photos.map((photo) => (
                    <div
                      key={photo.id}
                      className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
                        selectedPhotos.includes(photo.id)
                          ? 'border-purple-600'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div
                        className="cursor-pointer"
                        onClick={() => togglePhotoSelection(photo.id)}
                      >
                        <img
                          src={`${API}/events/${eventId}/photos/${photo.id}/download`}
                          alt="Event photo"
                          className="w-full h-48 object-cover"
                        />
                      </div>
                      
                      {/* Selection Checkbox */}
                      <div
                        className="absolute top-2 left-2 cursor-pointer"
                        onClick={() => togglePhotoSelection(photo.id)}
                      >
                        {selectedPhotos.includes(photo.id) ? (
                          <div className="w-6 h-6 bg-purple-600 rounded flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 bg-white border-2 border-gray-300 rounded"></div>
                        )}
                      </div>

                      {/* Photo Actions */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs mb-2">{photo.uploaderName}</p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => downloadPhoto(photo.id, photo.fileName)}
                            size="sm"
                            className="flex-1 bg-white/20 hover:bg-white/30 text-white"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button
                            onClick={() => deletePhoto(photo.id)}
                            size="sm"
                            className="flex-1 bg-red-500/80 hover:bg-red-600/80 text-white"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </div>

      {/* Edit Event Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">Edit Event</h3>
              <Button onClick={() => setIsEditing(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Event Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Date *</label>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Start Time *</label>
                  <input
                    type="time"
                    value={editForm.time}
                    onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Location *</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Category *</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="Conference">Conference</option>
                  <option value="Workshop">Workshop</option>
                  <option value="Seminar">Seminar</option>
                  <option value="Wedding">Wedding</option>
                  <option value="Birthday Party">Birthday Party</option>
                  <option value="Concert">Concert</option>
                  <option value="Festival">Festival</option>
                  <option value="Sports Event">Sports Event</option>
                  <option value="Corporate Event">Corporate Event</option>
                  <option value="Networking">Networking</option>
                  <option value="Exhibition">Exhibition</option>
                  <option value="Trade Show">Trade Show</option>
                  <option value="Charity Event">Charity Event</option>
                  <option value="Social Gathering">Social Gathering</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="4"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Cover Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleEditCoverImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {editCoverImagePreview && (
                  <div className="mt-2">
                    <img src={editCoverImagePreview} alt="Cover preview" className="w-full h-48 object-cover rounded-lg" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Capacity (optional)</label>
                  <input
                    type="number"
                    value={editForm.capacity}
                    onChange={(e) => setEditForm({ ...editForm, capacity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Ticket Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.ticketPrice}
                    onChange={(e) => setEditForm({ ...editForm, ticketPrice: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <Button
                onClick={() => setIsEditing(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveEventChanges}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
